/** Demo Mode only - shared filter bar used across every Finance tab (Overview/Forecast/
 * Profit & Loss/Contracts/EBIT Analysis). Only surfaces filters backed by real dataset
 * dimensions (see src/mocks/data/financeData.ts) - no invented filter axes. */

import { useMemo } from 'react';
import { Box, MenuItem, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FINANCE_CONTRACTS, FINANCIAL_YEAR_LABEL } from '../../../mocks/data/financeData';
import { useFinanceFilters } from '../FinanceFiltersContext';
import { ComparisonBasis, FinanceView, ScopeLevel } from '../types';

const FinanceFilterBar = () => {
  const { t } = useTranslation('common');
  const {
    scopeLevel, scopeId, view, comparison, sector, region, commercialModel,
    setScopeLevel, setScopeId, setView, setComparison, setSector, setRegion, setCommercialModel, clearFilters,
  } = useFinanceFilters();

  const clients = useMemo(() => Array.from(new Set(FINANCE_CONTRACTS.map((c) => c.client))).sort(), []);
  const contracts = useMemo(() => FINANCE_CONTRACTS.filter((c) => scopeLevel !== 'client' || c.client === scopeId), [scopeLevel, scopeId]);
  const sectors = useMemo(() => Array.from(new Set(FINANCE_CONTRACTS.map((c) => c.sector))).sort(), []);
  const regions = useMemo(() => Array.from(new Set(FINANCE_CONTRACTS.map((c) => c.region))).sort(), []);
  const commercialModels = useMemo(() => Array.from(new Set(FINANCE_CONTRACTS.map((c) => c.commercialModel))).sort(), []);

  const handleScopeLevelChange = (level: ScopeLevel) => {
    setScopeLevel(level);
    setScopeId('all');
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 4 }}>
      <TextField
        select
        label={t('financeFinancialYear', 'Financial year')}
        value={FINANCIAL_YEAR_LABEL}
        size="small"
        disabled
        sx={{ minWidth: 150 }}
      >
        <MenuItem value={FINANCIAL_YEAR_LABEL}>{FINANCIAL_YEAR_LABEL}</MenuItem>
      </TextField>

      <TextField
        select
        label={t('financeView', 'View')}
        value={view}
        onChange={(e) => setView(e.target.value as FinanceView)}
        size="small"
        sx={{ minWidth: 140 }}
      >
        <MenuItem value="ytd">{t('financeViewYtd', 'Year to date')}</MenuItem>
        <MenuItem value="fullYear">{t('financeViewFullYear', 'Full year')}</MenuItem>
      </TextField>

      <TextField
        select
        label={t('financeComparison', 'Comparison')}
        value={comparison}
        onChange={(e) => setComparison(e.target.value as ComparisonBasis)}
        size="small"
        sx={{ minWidth: 170 }}
      >
        <MenuItem value="budget">{t('financeComparisonBudget', 'Budget')}</MenuItem>
        <MenuItem value="previousForecast">{t('financeComparisonPreviousForecast', 'Previous forecast')}</MenuItem>
        <MenuItem value="priorYear">{t('financeComparisonPriorYear', 'Prior year')}</MenuItem>
        <MenuItem value="actual">{t('financeComparisonActual', 'Actual to date')}</MenuItem>
      </TextField>

      <TextField
        select
        label={t('financeScopeLevel', 'Scope')}
        value={scopeLevel}
        onChange={(e) => handleScopeLevelChange(e.target.value as ScopeLevel)}
        size="small"
        sx={{ minWidth: 130 }}
      >
        <MenuItem value="company">{t('financeScopeCompany', 'Company')}</MenuItem>
        <MenuItem value="client">{t('financeScopeClient', 'Client')}</MenuItem>
        <MenuItem value="contract">{t('financeScopeContract', 'Contract')}</MenuItem>
      </TextField>

      {scopeLevel === 'client' && (
        <TextField
          select
          label={t('financeClient', 'Client')}
          value={scopeId === 'all' ? '' : scopeId}
          onChange={(e) => setScopeId(e.target.value)}
          size="small"
          sx={{ minWidth: 220 }}
        >
          {clients.map((client) => (
            <MenuItem key={client}
              value={client}>{client}</MenuItem>
          ))}
        </TextField>
      )}

      {scopeLevel === 'contract' && (
        <TextField
          select
          label={t('financeContract', 'Contract')}
          value={scopeId === 'all' ? '' : scopeId}
          onChange={(e) => setScopeId(e.target.value)}
          size="small"
          sx={{ minWidth: 260 }}
        >
          {contracts.map((contract) => (
            <MenuItem key={contract.id}
              value={contract.id}>{`${contract.client} - ${contract.contractName}`}</MenuItem>
          ))}
        </TextField>
      )}

      <TextField
        select
        label={t('financeSector', 'Sector')}
        value={sector}
        onChange={(e) => setSector(e.target.value as typeof sector)}
        size="small"
        sx={{ minWidth: 170 }}
      >
        <MenuItem value="">{t('financeAllSectors', 'All sectors')}</MenuItem>
        {sectors.map((s) => (
          <MenuItem key={s}
            value={s}>{s}</MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label={t('financeRegion', 'Region')}
        value={region}
        onChange={(e) => setRegion(e.target.value)}
        size="small"
        sx={{ minWidth: 170 }}
      >
        <MenuItem value="">{t('financeAllRegions', 'All regions')}</MenuItem>
        {regions.map((r) => (
          <MenuItem key={r}
            value={r}>{r}</MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label={t('financeCommercialModel', 'Commercial model')}
        value={commercialModel}
        onChange={(e) => setCommercialModel(e.target.value as typeof commercialModel)}
        size="small"
        sx={{ minWidth: 190 }}
      >
        <MenuItem value="">{t('financeAllCommercialModels', 'All commercial models')}</MenuItem>
        {commercialModels.map((m) => (
          <MenuItem key={m}
            value={m}>{m}</MenuItem>
        ))}
      </TextField>

      <Box
        component="button"
        onClick={clearFilters}
        sx={{
          border: 'none', background: 'none', color: 'primary.main', cursor: 'pointer', fontSize: '13px', fontWeight: 500, p: 0,
        }}
      >
        {t('financeClearFilters', 'Clear filters')}
      </Box>
    </Box>
  );
};

export default FinanceFilterBar;
